import * as React from 'react';
import {
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
  type ColumnDef,
  type SortingState,
} from '@tanstack/react-table';
import { useVirtualizer } from '@tanstack/react-virtual';
import { ArrowDown, ArrowUp, ChevronsUpDown } from 'lucide-react';
import type { Posicao } from '@wms/shared';
import { ESTILO_STATUS } from '@/lib/status';
import { cn, compararNatural, numero } from '@/lib/utils';
import type { ColunaCsv } from '@/lib/csv';
import { normalizarCorredor, rotuloNivel } from '@/lib/layout-mapa';
import { Badge } from '@/componentes/ui/primitivos';
import type { SkuNaPosicao } from '@/hooks/consultas';
import { FiltroColuna, type OpcaoFiltro } from './FiltroColuna';

/**
 * Uma linha da grade — e do Excel: uma por POSIÇÃO × SKU, com a quantidade
 * daquele SKU naquela posição. A operação soma por linha; o total da posição
 * não aparece. Posição vazia sai numa linha com o SKU em branco, para a lista
 * continuar mostrando todas as posições.
 */
export interface LinhaEstoque {
  p: Posicao;
  sku: number | null;
  descricao: string;
  ean: string | null;
  quantidade: number | null;
}

/**
 * Abre as posições em linhas por SKU. Com uma busca por SKU, só as linhas
 * dele: as outras da mesma posição não são o que se procurou.
 */
export function montarLinhas(
  posicoes: readonly Posicao[],
  porPosicao: Map<number, SkuNaPosicao[]>,
  skuBuscado: number | null,
): LinhaEstoque[] {
  return posicoes.flatMap((p): LinhaEstoque[] => {
    const itens = (porPosicao.get(p.localizador) ?? []).filter(
      (i) => skuBuscado === null || i.produto === skuBuscado,
    );
    if (itens.length === 0) return [{ p, sku: null, descricao: '', ean: null, quantidade: null }];
    return itens.map((i) => ({ p, sku: i.produto, descricao: i.descricao, ean: i.ean, quantidade: i.saldo }));
  });
}

const nivel = (p: Posicao): string => (p.geometriaConfiavel ? rotuloNivel(p.nivelRack) : '');

/**
 * O valor de cada coluna como TEXTO, que é o que o filtro de coluna compara e
 * lista. É o mesmo texto que a célula mostra (o status pelo rótulo, não pelo
 * código), para a caixa do filtro dizer o que a pessoa vê na grade.
 */
export const VALOR_COLUNA: Record<string, (l: LinhaEstoque) => string> = {
  sku: (l) => (l.sku === null ? '' : String(l.sku)),
  descricao: (l) => l.descricao,
  quantidade: (l) => (l.quantidade === null ? '' : String(l.quantidade)),
  ean: (l) => l.ean ?? '',
  localizador: (l) => String(l.p.localizador),
  endereco: (l) => l.p.descricao || String(l.p.localizador),
  empresa: (l) => String(l.p.empresa),
  corredor: (l) => normalizarCorredor(l.p.corredor),
  modulo: (l) => l.p.modulo,
  box: (l) => l.p.box,
  nivel: (l) => nivel(l.p),
  status: (l) => ESTILO_STATUS[l.p.status].rotulo,
  skus: (l) => String(l.p.skus),
  totalPosicao: (l) => String(l.p.saldo),
};

export const ROTULO_COLUNA: Record<string, string> = {
  sku: 'SKU',
  descricao: 'descrição',
  quantidade: 'quantidade',
  ean: 'EAN',
  localizador: 'localizador',
  endereco: 'endereço',
  empresa: 'empresa',
  corredor: 'corredor',
  modulo: 'módulo',
  box: 'box',
  nivel: 'nível',
  status: 'status',
  skus: 'SKUs na posição',
  totalPosicao: 'total na posição',
};

const texto = (id: string): ((l: LinhaEstoque) => string) => VALOR_COLUNA[id] ?? (() => '');

/**
 * O Excel sai com AS MESMAS colunas e na mesma ordem da grade: SKU, Descrição,
 * Quantidade, EAN, Localizador (a ordem que a operação pediu) e o resto depois.
 */
export const COLUNAS_CSV: ColunaCsv<LinhaEstoque>[] = [
  { cabecalho: 'SKU', valor: (l) => l.sku ?? '', texto: true },
  { cabecalho: 'Descricao', valor: (l) => l.descricao },
  // Só deste SKU, só nesta posição — não é o saldo do SKU no CD.
  { cabecalho: 'Quantidade', valor: (l) => l.quantidade ?? '' },
  // Texto: EAN de 13 dígitos como número vira 7,89E+12 no Excel e perde dígitos.
  { cabecalho: 'EAN', valor: (l) => l.ean ?? '', texto: true },
  { cabecalho: 'Localizador', valor: (l) => l.p.localizador, texto: true },
  { cabecalho: 'Endereco', valor: texto('endereco') },
  { cabecalho: 'Empresa', valor: (l) => l.p.empresa },
  { cabecalho: 'Corredor', valor: texto('corredor') },
  { cabecalho: 'Modulo', valor: texto('modulo') },
  { cabecalho: 'Box', valor: texto('box') },
  { cabecalho: 'Nivel', valor: texto('nivel') },
  { cabecalho: 'Status', valor: texto('status') },
  { cabecalho: 'SKUsNaPosicao', valor: (l) => l.p.skus },
  // Soma de TODOS os SKUs da posição — repete em cada linha da mesma posição.
  { cabecalho: 'QtdTotalPosicao', valor: (l) => l.p.saldo },
];

export type FiltrosColuna = Record<string, string[]>;

export interface PropsTabelaEstoque {
  linhas: readonly LinhaEstoque[];
  selecionado: number | null;
  aoSelecionar: (localizador: number) => void;
  /** Filtro estilo Excel por coluna; quem filtra as linhas é a página. */
  filtros?: FiltrosColuna | undefined;
  aoFiltrar?: ((coluna: string, valores: string[] | null) => void) | undefined;
  opcoesFiltro?: ((coluna: string) => OpcaoFiltro[]) | undefined;
}

const ALTURA_LINHA = 46; // > alvo de toque, com respiro

/** Coluna de texto simples, com o mesmo valor do filtro e do Excel. */
function coluna(id: string, header: string, size: number, classe?: string): ColumnDef<LinhaEstoque> {
  const valor = texto(id);
  return {
    id,
    header,
    accessorFn: valor,
    sortingFn: (a, b) => compararNatural(valor(a.original), valor(b.original)),
    cell: (info) => <span className={classe}>{valor(info.row.original)}</span>,
    size,
  };
}

const COLUNAS: ColumnDef<LinhaEstoque>[] = [
  {
    id: 'sku',
    header: 'SKU',
    accessorFn: (l) => l.sku ?? undefined,
    sortUndefined: 'last',
    cell: (info) => (
      <span className="tabular font-mono font-semibold">{info.row.original.sku ?? ''}</span>
    ),
    size: 100,
  },
  {
    ...coluna('descricao', 'Descrição', 280),
    // Posição sem produto: uma nota discreta no lugar da descrição, em vez de
    // uma linha em branco. Só na grade — o Excel sai com a célula vazia.
    cell: (info) => {
      const l = info.row.original;
      if (l.sku !== null) return l.descricao;
      return (
        <span className="text-[12px] italic text-textoFraco">
          {l.p.status === 'livre' ? 'Livre para guardar' : 'Sem produto'}
        </span>
      );
    },
  },
  {
    id: 'quantidade',
    header: 'Quantidade',
    accessorFn: (l) => l.quantidade ?? undefined,
    sortUndefined: 'last',
    cell: (info) => {
      const q = info.row.original.quantidade;
      return q === null ? null : (
        <span className={cn('tabular block text-right font-medium', q < 0 && 'text-status-divergencia')}>
          {numero(q)}
        </span>
      );
    },
    size: 110,
  },
  coluna('ean', 'EAN', 140, 'tabular font-mono'),
  {
    // Código do localizador no PBS: é a chave que a operação confere
    // contra o ERP. O "endereço" legível é a DESCRICAO.
    id: 'localizador',
    header: 'Localizador',
    accessorFn: (l) => l.p.localizador,
    cell: (info) => (
      <span className="tabular font-mono text-textoFraco">{info.row.original.p.localizador}</span>
    ),
    size: 110,
  },
  coluna('endereco', 'Endereço', 170, 'font-mono font-semibold'),
  { ...coluna('empresa', 'Empresa', 90), accessorFn: (l) => l.p.empresa, sortingFn: 'basic' },
  coluna('corredor', 'Corredor', 100),
  coluna('modulo', 'Módulo', 80),
  coluna('box', 'Box', 70),
  coluna('nivel', 'Nível', 70),
  {
    id: 'status',
    header: 'Status',
    accessorFn: (l) => l.p.status,
    cell: (info) => {
      const estilo = ESTILO_STATUS[info.row.original.p.status];
      return (
        <Badge className={estilo.classeBadge}>
          <span aria-hidden>{estilo.glifo}</span>
          {estilo.rotulo}
        </Badge>
      );
    },
    size: 140,
  },
  {
    id: 'skus',
    header: 'SKUs na posição',
    accessorFn: (l) => l.p.skus,
    cell: (info) => <span className="tabular block text-right">{numero(info.row.original.p.skus)}</span>,
    size: 130,
  },
  {
    // Soma de todos os SKUs da posição: repete em cada linha da mesma posição.
    id: 'totalPosicao',
    header: 'Total na posição',
    accessorFn: (l) => l.p.saldo,
    cell: (info) => {
      const t = info.row.original.p.saldo;
      return (
        <span className={cn('tabular block text-right', t < 0 && 'text-status-divergencia')}>
          {numero(t)}
        </span>
      );
    },
    size: 130,
  },
];

export function TabelaEstoque({
  linhas: dados,
  selecionado,
  aoSelecionar,
  filtros,
  aoFiltrar,
  opcoesFiltro,
}: PropsTabelaEstoque): JSX.Element {
  const [ordenacao, definirOrdenacao] = React.useState<SortingState>([
    { id: 'endereco', desc: false },
  ]);
  const colunas = COLUNAS;

  const tabela = useReactTable({
    data: dados as LinhaEstoque[],
    columns: colunas,
    state: { sorting: ordenacao },
    onSortingChange: definirOrdenacao,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  const container = React.useRef<HTMLDivElement | null>(null);
  const linhas = tabela.getRowModel().rows;

  const virtualizador = useVirtualizer({
    count: linhas.length,
    getScrollElement: () => container.current,
    estimateSize: () => ALTURA_LINHA,
    overscan: 12,
  });

  // A DESCRIÇÃO é elástica: com colunas só fixas sobra tela vazia à direita
  // em monitor largo.
  const grade = colunas
    .map((c) => (c.id === 'descricao' ? `minmax(${c.size ?? 200}px,1fr)` : `${c.size ?? 120}px`))
    .join(' ');

  return (
    <div className="flex h-full min-h-0 flex-col overflow-hidden rounded-2xl border border-borda bg-superficie shadow-cartao">
      <div className="flex min-h-0 flex-1 flex-col overflow-x-auto">
        <div role="table" aria-rowcount={linhas.length} className="flex min-h-0 w-full min-w-max flex-1 flex-col">
          <div role="rowgroup" className="sticky top-0 z-10 shrink-0 bg-superficie2">
            {tabela.getHeaderGroups().map((grupo) => (
              <div
                role="row"
                key={grupo.id}
                className="grid items-center border-b border-borda"
                style={{ gridTemplateColumns: grade }}
              >
                {grupo.headers.map((cabecalho) => {
                  const direcao = cabecalho.column.getIsSorted();
                  const id = cabecalho.column.id;
                  return (
                    <div role="columnheader" key={cabecalho.id} className="flex items-center pr-1.5" aria-sort={
                      direcao === 'asc' ? 'ascending' : direcao === 'desc' ? 'descending' : 'none'
                    }>
                      <button
                        type="button"
                        onClick={cabecalho.column.getToggleSortingHandler()}
                        className="flex min-h-[42px] min-w-0 flex-1 items-center gap-1 pl-3.5 text-left text-[11px] font-bold uppercase tracking-[0.04em] text-textoFraco transition-colors hover:text-primaria"
                      >
                        {flexRender(cabecalho.column.columnDef.header, cabecalho.getContext())}
                        {direcao === 'asc' ? (
                          <ArrowUp className="h-3 w-3" />
                        ) : direcao === 'desc' ? (
                          <ArrowDown className="h-3 w-3" />
                        ) : (
                          <ChevronsUpDown className="h-3 w-3 opacity-40" />
                        )}
                      </button>
                      {aoFiltrar && opcoesFiltro && VALOR_COLUNA[id] ? (
                        <FiltroColuna
                          titulo={String(cabecalho.column.columnDef.header)}
                          opcoes={() => opcoesFiltro(id)}
                          selecionados={filtros?.[id] ?? null}
                          aoAplicar={(v) => aoFiltrar(id, v)}
                        />
                      ) : null}
                    </div>
                  );
                })}
              </div>
            ))}
          </div>

          <div ref={container} role="rowgroup" className="min-h-0 flex-1 overflow-y-auto">
            <div style={{ height: virtualizador.getTotalSize(), position: 'relative' }}>
              {virtualizador.getVirtualItems().map((item) => {
                const linha = linhas[item.index];
                if (!linha) return null;
                const posicao = linha.original.p;
                return (
                  <div
                    role="row"
                    key={linha.id}
                    aria-selected={posicao.localizador === selecionado}
                    tabIndex={0}
                    onClick={() => aoSelecionar(posicao.localizador)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        aoSelecionar(posicao.localizador);
                      }
                    }}
                    className={cn(
                      'absolute inset-x-0 grid cursor-pointer items-center border-b border-borda text-[13px] transition-colors',
                      item.index % 2 === 1 && 'bg-superficie2',
                      'hover:bg-primaria-suave/60',
                      posicao.localizador === selecionado &&
                        'bg-primaria-suave shadow-[inset_3px_0_0_rgb(var(--cor-primaria))]',
                    )}
                    style={{
                      height: item.size,
                      transform: `translateY(${item.start}px)`,
                      gridTemplateColumns: grade,
                    }}
                  >
                    {linha.getVisibleCells().map((celula) => (
                      <div role="cell" key={celula.id} className="truncate px-3.5">
                        {flexRender(celula.column.columnDef.cell, celula.getContext())}
                      </div>
                    ))}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
